import { createFileRoute, Link } from "@tanstack/react-router";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { Reveal } from "@/components/site/wow/Reveal";
import { ArrowRight } from "lucide-react";
import { aeoGeo } from "@/lib/seo/schema";

export const Route = createFileRoute("/en_/about")({
  head: () =>
    ({
      meta: [
        { title: "About Fenny — Premium video on subscription" },
        {
          name: "description",
          content:
            "Fenny is a smarter alternative to traditional film production. We produce premium video on subscription for marketing teams that want continuous output.",
        },
        { property: "og:title", content: "About Fenny" },
        {
          property: "og:description",
          content:
            "A smarter alternative to traditional film production. Premium video on subscription for marketing teams.",
        },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:url", content: "https://fenny.se/en/about" },
      ],
      links: [
        { rel: "canonical", href: "https://fenny.se/en/about" },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/about", name: "About Fenny — Premium video on subscription", description: "Fenny is a smarter alternative to traditional film production. We produce premium video on subscription for marketing teams that want continuous output."})) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Fenny",
            url: "https://fenny.se/en/about",
            logo: "https://fenny.se/favicon.ico",
            description:
              "Premium video on subscription for marketing teams. Part of Norrhavet Group.",
            parentOrganization: { "@type": "Organization", name: "Norrhavet Group" },
          }),
        },
      ],
    }) as never,
  component: AboutPage,
});

function AboutPage() {
  return (
    <div>
      <section className="relative overflow-hidden pt-32 pb-12 md:pt-44 md:pb-20">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_80%_10%,color-mix(in_oklab,var(--butter)_12%,transparent),transparent_70%)]"
        />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="mb-6 text-xs uppercase tracking-[0.22em] text-butter">
            About Fenny
          </div>
          <KineticHeading
            text="We make video for people — not for the algorithm."
            className="max-w-[20ch] text-balance text-5xl font-semibold leading-[0.98] text-foreground md:text-7xl"
            highlight={{ indices: [3, 4], className: "text-mint italic font-serif font-normal" }}
          />
          <p className="mt-10 max-w-2xl text-pretty text-lg text-muted-foreground md:text-xl">
            Fenny started in 2023 as an answer to something we were tired of:
            film production that takes too long, costs unpredictably and ships
            too rarely. We flipped it — subscription, fixed price, rhythm.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Magnetic strength={0.3}>
              <Link
                to="/en/pricing"
                className="group inline-flex items-center gap-3 rounded-full bg-mint px-8 py-4 text-base font-semibold text-mint-foreground hover:shadow-[0_20px_60px_-12px] hover:shadow-mint/50"
              >
                Book a strategy call
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Magnetic>
            <Link
              to="/en/services"
              className="inline-flex items-center gap-2 text-sm text-foreground/80 underline-offset-4 hover:text-butter hover:underline"
            >
              See our services →
            </Link>
          </div>
        </div>
      </section>

      <Reveal>
        <section className="py-24 md:py-32">
          <div className="container-wide">
            <div className="grid gap-12 md:grid-cols-3">
              {[
                {
                  title: "Rhythm beats blockbusters",
                  body: "One great film a year rarely moves the needle. A steady cadence does. Pulse keeps your channels alive every month.",
                },
                {
                  title: "Fixed price, no surprises",
                  body: "You know what it costs before we start. No change orders. No hidden overtime. Predictable budgets your CFO will thank you for.",
                },
                {
                  title: "Senior craft, end to end",
                  body: "The same producers, directors and editors from brief to delivery. No juniors babysitting your brand.",
                },
              ].map((v) => (
                <div
                  key={v.title}
                  className="rounded-3xl border border-white/10 bg-surface p-8"
                >
                  <h3 className="text-xl font-semibold text-foreground">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {v.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section className="py-24 md:py-32 border-t border-white/10">
          <div className="container-wide">
            <div className="grid gap-12 md:grid-cols-2 md:items-start">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-butter">
                  Norrhavet Group
                </div>
                <h2 className="mt-4 text-balance text-3xl font-semibold leading-tight md:text-5xl">
                  Fenny is video. Group is the full chain.
                </h2>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">
                  We sit under the same roof as Norrhavet and Ellary. If you
                  also need branding, a landing page that matches the campaign,
                  or paid media that scales — it's the same team, the same PM,
                  the same invoice.
                </p>
              </div>
              <blockquote className="relative rounded-3xl border border-white/10 bg-surface p-8 md:p-10">
                <div
                  aria-hidden
                  className="absolute -top-6 -left-2 text-7xl font-serif text-mint/30"
                >
                  "
                </div>
                <p className="relative text-pretty text-xl font-medium leading-snug text-foreground md:text-2xl">
                  I've rarely experienced anything like it. Fenny takes
                  ownership, thinks further ahead and delivers with a finesse
                  that almost feels too good to be true.
                </p>
                <footer className="mt-6 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Maria Borg</span>
                  {" · "}Garageportexperten
                </footer>
              </blockquote>
            </div>
          </div>
        </section>
      </Reveal>

      <Reveal>
        <section className="py-24 md:py-32 border-t border-white/10">
          <div className="container-wide text-center">
            <h2 className="mx-auto max-w-3xl text-balance text-4xl font-semibold leading-tight md:text-6xl">
              Ready to make video the easy part of your marketing?
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Magnetic strength={0.3}>
                <Link
                  to="/en/contact"
                  className="group inline-flex items-center gap-3 rounded-full bg-mint px-8 py-4 text-base font-semibold text-mint-foreground"
                >
                  Get in touch
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Magnetic>
              <Link
                to="/en/pricing"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-medium text-foreground/90 hover:bg-white/5"
              >
                See pricing
              </Link>
            </div>
          </div>
        </section>
      </Reveal>
    </div>
  );
}
